import { useEffect, useMemo, useRef, startTransition, useState } from 'react'
import { type OptionChainRow, useMarketStore } from '../services/store'
import { useSettingsStore } from '../stores/settingsStore'
import type { OptionData } from '../types'
// marketDataBus removed — history reads from Zustand directly

import { extractTimeMinutes } from '../lib/snapshot-search'

const EMPTY_ARRAY: any[] = []

// ─── SIDE-EFFECT-FREE CACHE ───
// Previously this hook mutated Zustand-held snapshot objects directly
// (snap._istMinutesCache = ...), which corrupts store identity tracking.
// Now the IST-minutes computation is cached in a WeakMap keyed by snapshot
// reference, leaving the store data untouched.
const istMinutesCache = new WeakMap<object, number>()

const getIstMinutes = (snap: any): number => {
    if (istMinutesCache.has(snap)) return istMinutesCache.get(snap)!
    const mins = extractTimeMinutes(snap)
    istMinutesCache.set(snap, mins)
    return mins
}

export const useHistoryController = () => {
    const symbol = useSettingsStore((state) => state.symbol)
    const historicalDate = useSettingsStore((state) => state.historicalDate)
    const simulationTime = useSettingsStore((state) => state.simulationTime)
    const setIsLive = useSettingsStore((state) => state.setIsLive)
    const inspectionSource = useSettingsStore((state) => state.inspectionSource)

    // Restored from 0e75c01: Select history directly from Zustand store
    const history = useMarketStore( ( state ) => {
        if ( inspectionSource === 'HistoricalSnapshot' ) return state.historicalHistory?.[ symbol ]
        if ( inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD' ) return state.rangeHistory?.[ symbol ]
        return state.intradayHistory?.[ symbol ]
    } )

    const rawSnapshotsRef = useRef<any[]>(EMPTY_ARRAY)
    const lastRawRef = useRef<any[]>(EMPTY_ARRAY)

    useEffect(() => {
        rawSnapshotsRef.current = history || EMPTY_ARRAY
    }, [history])

    // 1. Processing Logic: AVOID EXPENSIVE { ...snap } COPIES
    const processedSnapshotsRef = useRef<any[]>([])
    const [historyTick, setHistoryTick] = useState(0)
    
    // Update processed snapshots when raw snapshots change (checked via ref)
    useEffect(() => {
        const interval = setInterval(() => {
            const rawSnapshots = rawSnapshotsRef.current
            if (rawSnapshots === lastRawRef.current) return
            lastRawRef.current = rawSnapshots
            
            if (rawSnapshots.length === 0) {
                processedSnapshotsRef.current = []
                setHistoryTick(t => t + 1)
                return
            }
            if (inspectionSource !== 'CurrentIntraday' && !historicalDate) {
                processedSnapshotsRef.current = []
                setHistoryTick(t => t + 1)
                return
            }

            processedSnapshotsRef.current = rawSnapshots.filter((snap: any) => {
                const mins = getIstMinutes(snap)
                return !isNaN(mins)
            })
            setHistoryTick(t => t + 1)
        }, 2000) // Check every 2 seconds

        return () => clearInterval(interval)
    }, [historicalDate, inspectionSource])

    // Update Live Status
    useEffect(() => {
        if (!historicalDate) {
            setIsLive(true)
        } else {
            setIsLive(false)
        }
    }, [historicalDate, setIsLive])

    // 2. Playback / Time Jump Logic
    const lastProcessedSnapRef = useRef<string | number | null>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        // FREEZE FIX v2: Debounce by 200ms.
        // When DataBus notifies subscribers, useHistoryController, useCogEngine,
        // and useMarketWatchEngine all fire simultaneously. This debounce ensures
        // we wait for the initial cascade to settle before running our heavy
        // chain-mapping loop + updateTick (which triggers 15 more re-renders).
        if (debounceRef.current) clearTimeout(debounceRef.current)

        debounceRef.current = setTimeout(() => {
            const processedSnapshots = processedSnapshotsRef.current
            if (processedSnapshots.length === 0) return
            if (inspectionSource !== 'CurrentIntraday' && !historicalDate) return

            // ═══════════════════════════════════════════════════════════════════════
            // FREEZE FIX v3: SKIP updateTick in LIVE mode.
            // In live mode (CurrentIntraday), GlobalMarketEngine already provides
            // fresh chain data from the Upstox API. useHistoryController was:
            //   1. Overwriting fresh live data with stale CSV snapshot data
            //   2. Triggering: Zustand data[] change → rawChain selector
            //      → Context recomputation → ALL 13 consumers re-render
            // This single guard eliminates an entire re-render cascade wave (~260ms).
            // The updateTick is only needed for HistoricalSnapshot playback.
            // ═══════════════════════════════════════════════════════════════════════
            if (inspectionSource === 'CurrentIntraday') return

            const [h, m] = simulationTime.split(':').map(Number)
            const targetMinutes = (h ?? 0) * 60 + (m ?? 0)

            // Find the closest snapshot to simulationTime using O(log N) binary search
            let bestSnap: any = null
            if (processedSnapshots.length > 0) {
                let left = 0
                let right = processedSnapshots.length - 1
                while (left <= right) {
                    const mid = Math.floor((left + right) / 2)
                    const s = processedSnapshots[mid]
                    const sMins = getIstMinutes(s)
                    if (sMins === targetMinutes) {
                        bestSnap = s
                        break
                    }
                    if (sMins < targetMinutes) {
                        left = mid + 1
                    } else {
                        right = mid - 1
                    }
                }
                if (!bestSnap) {
                    let bestDiff = Infinity
                    if (left < processedSnapshots.length) {
                        const diff = Math.abs(getIstMinutes(processedSnapshots[left]) - targetMinutes)
                        if (diff < bestDiff) { bestDiff = diff; bestSnap = processedSnapshots[left] }
                    }
                    if (right >= 0) {
                        const diff = Math.abs(getIstMinutes(processedSnapshots[right]) - targetMinutes)
                        if (diff < bestDiff) { bestSnap = processedSnapshots[right] }
                    }
                }
            }

            if (bestSnap) {
                const bestSnapMins = getIstMinutes(bestSnap)
                const snapId = bestSnap.timestampIST || bestSnap.timestamp || bestSnap.time || bestSnapMins

                // Prevent redundant mapping and state updates if same snapshot
                if (lastProcessedSnapRef.current === snapId) {
                    return
                }
                lastProcessedSnapRef.current = snapId

                console.log(`[useHistoryController] Sliced to ${bestSnapMins} mins for ${simulationTime} (${inspectionSource})`);

                // FREEZE FIX v2: Defer the heavy chain-mapping + Zustand updateTick
                // to requestIdleCallback so it runs when the browser is idle,
                // NOT during the initial render cascade.
                const runMapping = () => {
                    const s = bestSnap

                    const chainMap = new Map<number, OptionChainRow>()

                    // FORMAT 1: NEW D1 DATABASE FORMAT (Raw Upstox strikeMap payload)
                    if (s.data?.strategyChainData?.strikeMap) {
                        const strikeMap = s.data.strategyChainData.strikeMap
                        for (const strikeStr of Object.keys(strikeMap)) {
                            const strikeNum = parseInt(strikeStr, 10)
                            const opt = strikeMap[strikeStr]

                            const ceData = opt.callOptionData?.marketData
                            const peData = opt.putOptionData?.marketData
                            const ceGreeks = opt.callOptionData?.analytics || {}
                            const peGreeks = opt.putOptionData?.analytics || {}

                            chainMap.set(strikeNum, {
                                strike: strikeNum,
                                ce: {
                                    ltp: ceData?.ltp || 0,
                                    oi: ceData?.oi || 0,
                                    vol: ceData?.volume || 0,
                                    oiChange: ceData ? ceData.oi - (ceData.prevOi || ceData.oi) : 0,
                                    change: ceData ? ceData.ltp - (ceData.cp || ceData.ltp) : 0,
                                    changePercent: ceData && ceData.cp ? ((ceData.ltp - ceData.cp) / ceData.cp) * 100 : 0,
                                    iv: ceGreeks.iv || 0,
                                    delta: ceGreeks.delta || 0,
                                    gamma: ceGreeks.gamma || 0,
                                    theta: ceGreeks.theta || 0,
                                    vega: ceGreeks.vega || 0,
                                },
                                pe: {
                                    ltp: peData?.ltp || 0,
                                    oi: peData?.oi || 0,
                                    vol: peData?.volume || 0,
                                    oiChange: peData ? peData.oi - (peData.prevOi || peData.oi) : 0,
                                    change: peData ? peData.ltp - (peData.cp || peData.ltp) : 0,
                                    changePercent: peData && peData.cp ? ((peData.ltp - peData.cp) / peData.cp) * 100 : 0,
                                    iv: peGreeks.iv || 0,
                                    delta: peGreeks.delta || 0,
                                    gamma: peGreeks.gamma || 0,
                                    theta: peGreeks.theta || 0,
                                    vega: peGreeks.vega || 0,
                                },
                            })
                        }
                    }
                    // FORMAT 2: LEGACY Google Sheet CSV FORMAT (Or Turso V1)
                    else if (s.optionsData?.[symbol]) {
                        const rawOptions = Object.values(s.optionsData[symbol]).flat() as OptionData[]
                        rawOptions.forEach((opt) => {
                            if (!chainMap.has(opt.strike)) {
                                chainMap.set(opt.strike, {
                                    strike: opt.strike,
                                    ce: { ltp: 0, oi: 0, vol: 0, oiChange: 0, change: 0, changePercent: 0 },
                                    pe: { ltp: 0, oi: 0, vol: 0, oiChange: 0, change: 0, changePercent: 0 },
                                })
                            }
                            const row = chainMap.get(opt.strike)!
                            row.ce = {
                                ltp: opt.callPrice,
                                oi: opt.callOpenInterest,
                                vol: opt.callVolume,
                                oiChange: opt.callOpenInterestChange || 0,
                                change: opt.callChange || 0,
                                changePercent: opt.callChangePercent,
                                iv: opt.callImpliedVolatility,
                                delta: opt.callDelta,
                                gamma: opt.callGamma,
                                theta: opt.callTheta,
                                vega: opt.callVega,
                            }
                            row.pe = {
                                ltp: opt.putPrice,
                                oi: opt.putOpenInterest,
                                vol: opt.putVolume,
                                oiChange: opt.putOpenInterestChange || 0,
                                change: opt.putChange || 0,
                                changePercent: opt.putChangePercent,
                                iv: opt.putImpliedVolatility,
                                delta: opt.putDelta,
                                gamma: opt.putGamma,
                                theta: opt.putTheta,
                                vega: opt.putVega,
                            }
                        })
                    }

                    const chainRows = Array.from(chainMap.values()).sort((a, b) => a.strike - b.strike)

                    // Extract Top-level info
                    let vix = s.vixData?.value || s.vix || 0
                    if (!vix && s.data?.optionChainExtendedData?.indiaVixLtpc?.ltp) {
                        vix = s.data.optionChainExtendedData.indiaVixLtpc.ltp
                    }

                    const meta = {
                        timestamp: (() => {
                            // Handle DD-MM-YYYY HH:MM:SS format (from HF CSV) which new Date() can't parse
                            const raw = s.timestamp || s.timestampIST || ''
                            if (typeof raw === 'string') {
                                const ddmmMatch = raw.match(/(\d{2})-(\d{2})-(\d{4})\s+(\d{2}:\d{2}:\d{2})/)
                                if (ddmmMatch) {
                                    const iso = `${ddmmMatch[3]}-${ddmmMatch[2]}-${ddmmMatch[1]}T${ddmmMatch[4]}+05:30`
                                    const parsed = new Date(iso).getTime()
                                    if (!isNaN(parsed)) return parsed
                                }
                            }
                            const fallback = new Date(raw).getTime()
                            return isNaN(fallback) ? Date.now() : fallback
                        })(),
                        dayChange: 0,
                        dayChangeP: 0,
                        vix: vix,
                        vixChange: s.vixData?.change || 0,
                    }

                    let indexLtp = s.indexLtp || s.indexData?.[symbol]?.lastPrice || s.spotPrice || 0
                    if (!indexLtp && s.data?.optionChainExtendedData?.assetLtpc?.ltp) {
                        indexLtp = s.data.optionChainExtendedData.assetLtpc.ltp
                    }

                    // FREEZE FIX v2: Wrap in startTransition so React treats this as
                    // a low-priority update, preventing it from blocking user interactions.
                    startTransition(() => {
                        useMarketStore.getState().updateTick(symbol, indexLtp, chainRows, meta)
                    })
                }

                // Use requestIdleCallback if available, otherwise setTimeout(0)
                if (typeof requestIdleCallback !== 'undefined') {
                    requestIdleCallback(runMapping, { timeout: 500 })
                } else {
                    setTimeout(runMapping, 16)
                }
            }
        }, 200) // 200ms debounce

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    // FREEZE FIX v4: read from ref, but trigger when historyTick increments
    }, [simulationTime, historicalDate, symbol, inspectionSource, historyTick])
}
